import {
	list,
	ListServiceTicketsResponse
} from "client/Api/Workspace/LiveInformation/ConnectWise/connectWiseLiveInformation";
import { subscribe, ConnectWiseEvent } from "./events";
import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";

interface ConnectWiseLiveInformationProviderOptions {
	intervalInSeconds: number;
	isConnectWiseConfigured: boolean;
	customerPortalUrl: string;
}

export type ConnectWiseResourceId = "tickets";

type Interests = {
	items?: boolean;
};

export type InterestedConnectWiseParty = InterestedParty<number, ConnectWiseResourceId> & {
	update(response: ListServiceTicketsResponse): void;
	informAboutConnectWiseNotBeingConfigured(): void;
	informAboutGlobalRetrievalFailure(): void;
	informAboutConsecutiveErrors(): void;
};

class ConnectWiseLiveInformationProvider extends LiveInformationProvider<
	ConnectWiseLiveInformationProviderOptions,
	number,
	ConnectWiseResourceId,
	Interests,
	InterestedConnectWiseParty,
	ListServiceTicketsResponse
> {
	public get isConnectWiseConfigured(): boolean {
		return this.options.isConnectWiseConfigured;
	}

	public get customerPortalUrl(): string {
		return this.options.customerPortalUrl;
	}

	constructor(options: ConnectWiseLiveInformationProviderOptions) {
		super(options);

		subscribe(ConnectWiseEvent.Created, (_, ticket) => {
			this.mutateCacheItem(
				"tickets",
				(x) => {
					if (x.wasGlobalFailure) {
						return x;
					}
					const copy = {
						...x,
						data: {
							...x.data,
							items: [...x.data.items]
						}
					};
					copy.data.items.splice(0, 0, ticket);
					return copy;
				}
			);
		});
	}

	protected async getDataFromBackend(
		entries: readonly [ConnectWiseResourceId, Interests][]
	): Promise<readonly [ConnectWiseResourceId, ListServiceTicketsResponse][]> {
		if (!this.isConnectWiseConfigured) {
			return Promise.reject();
		}

		const result: ListServiceTicketsResponse = await list();
		return entries.map(
			([resourceId]) => {
				switch (resourceId)
				{
					case "tickets":
						return [resourceId, result];
					default:
						throw new UnreachableCaseError(resourceId);
				}
			}
		);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<Interests, ListServiceTicketsResponse>,
		response: [Interests, ListServiceTicketsResponse]
	): void {
		const [retrievedInterests, model] = response;
		if (retrievedInterests.items) {
			cacheItem.retrievedInterests.items = true;
			cacheItem.data.items = model.items;
		}
	}

	protected override informAboutGlobalRetrievalFailure(party: InterestedConnectWiseParty): void {
		if (!this.isConnectWiseConfigured) {
			if (party.informAboutConnectWiseNotBeingConfigured) {
				party.informAboutConnectWiseNotBeingConfigured();
			}
			return;
		}

		super.informAboutGlobalRetrievalFailure(party);
	}

	protected informParty(
		party: InterestedConnectWiseParty,
		cacheItem: [Interests, ListServiceTicketsResponse]
	): void {
		if (!this.isConnectWiseConfigured) {
			if (party.informAboutConnectWiseNotBeingConfigured) {
				party.informAboutConnectWiseNotBeingConfigured();
			}
			return;
		}

		const [retrievedInterests, response] = cacheItem;
		const interestedIn: Interests = this.getInterestsForEnsuredParty(party);

		if (interestedIn.items && retrievedInterests.items) {
			if (party.update) {
				party.update(response);
			}
		}
	}
}

export {
	ConnectWiseLiveInformationProvider
};
